import {
  DefaultError,
  QueryKey,
  UndefinedInitialDataOptions,
  useQuery
} from '@tanstack/react-query';
import { fetch } from '../fetch';
import { schemas } from '@convergence/client';
import { useEffect } from 'react';

export const useAnalysisSchemasQuery = () => {
  const query = useQuery({
    queryKey: ['analysisSchemas'],
    queryFn: async () => {
      return fetch(
        '/analysis/schema',
        { method: 'GET' },
        schemas.AnalysisSchemasDto
      );
    },
    retry: false
  });

  return query;
};

export function useQueryLocalStorage<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  options: UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>
) {
  function getQueryKeyString(key: TQueryKey): string {
    return key.join(':');
  }

  function getFromLocalStorage(key: string): TQueryFnData | undefined {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const item = window.localStorage.getItem(key);
    if (!item) {
      return undefined;
    }
    try {
      return JSON.parse(item) as TQueryFnData;
    } catch (err) {
      return item as unknown as TQueryFnData;
    }
  }
  const initialData = getFromLocalStorage(getQueryKeyString(options.queryKey));
  const query = useQuery<TQueryFnData, TError, TData, TQueryKey>({
    ...options,
    initialData
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    } else if (query.data) {
      console.log('Storing query data in localStorage', {
        key: getQueryKeyString(options.queryKey),
        data: query.data
      });
      window.localStorage.setItem(
        getQueryKeyString(options.queryKey),
        JSON.stringify(query.data)
      );
    }
  }, [query.data]);

  return query;
}
